<template>
  <div class="container-full-screen">
    <div class="container-control-screen">
      <div class="wrapper-actions">
        <p class="small accent dim-dark">{{ t('label_language') }}</p>
        <ButtonDropdownLanguage v-model="data.languageCode" :is-global="isGlobal" :language-default />
        <div class="divider-small" />
      </div>

      <div class="wrapper-features">
        <p class="small accent dim-dark">{{ t('label_text_control') }}</p>
        <GaugeWithLabel
          v-model="data.textSizeMultiplier"
          :is-global="isGlobal"
          gauge-setting-global="textSizeMultiplier"
        >
          {{ t('text_size') }}: {{ data.textSizeMultiplier }}
        </GaugeWithLabel>
        <div class="divider-small" />
      </div>

      <div class="wrapper-features">
        <p class="small accent dim-dark">{{ t('screen_area_control') }}</p>
        <GaugeWithLabel
          v-model="data.screenScrollHeight"
          :is-global="isGlobal"
          gauge-setting-global="screenScrollHeight"
        >
          {{ t('screen_mode_message_height') }}: {{ data.screenScrollHeight }}%
        </GaugeWithLabel>

        <InputNumberPercent
          v-model="data.screenMessageWidth"
          :is-gsc="false"
          :is-global="isGlobal"
          :title-setting="t('screen_mode_message_width')"
          :text-value-current="t('screen_mode_message_width_current')"
          preference-target="screenMessageWidth"
        />

        <InputNumberWithButtons
          v-if="isVisibleControllerHeight"
          v-model="data.screenContainerHeight"
          :is-gsc="false"
          :is-global="isGlobal"
          :title-setting="t('screen_mode_container_height')"
          :text-value-current="t('screen_mode_message_height_current')"
          preference-target="screenContainerHeight"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import ButtonDropdownLanguage from '@/components/domains/chat/common/ButtonDropdownLanguage.vue';
import GaugeWithLabel from '@/components/domains/chat/common/GaugeWithLabel.vue';
import InputNumberPercent from '@/components/shared/composite/InputNumberPercent.vue';
import InputNumberWithButtons from '@/components/shared/composite/InputNumberWIthButtons.vue';
import { getLanguageByCode } from '@/static/languages';
import { useAudioStore } from '@/stores/audio';
import { useCallStore } from '@/stores/call';
import { useUserStore } from '@/stores/user';
import { Language } from '@/types/common';
import { ScreenPreferences } from '@/types/components';

const callStore = useCallStore();
const audioStore = useAudioStore();
const userStore = useUserStore();
const { t } = useI18n();

const props = withDefaults(defineProps<{ isGlobal?: boolean; isVisible?: boolean }>(), {
  isGlobal: false,
  isVisible: false,
});

const data = reactive<ScreenPreferences>({
  languageCode: null,
  textSizeMultiplier: userStore.userPreferences.textSizeMultiplier,
  screenMessageWidth: userStore.userPreferences.screenMessageWidth,
  screenScrollHeight: userStore.userPreferences.screenScrollHeight,
  screenContainerHeight: userStore.userPreferences.screenContainerHeight,
});

const payloadMessageFc = computed(() => ({
  generalAttribute: {
    loginId: 'any value',
    languageCode: 'ko',
  },
  screenAttribute: {
    ...data,
    languageCode: data.languageCode?.code,
    screenContainerHeight: data.screenContainerHeight ?? 0 / 16,
  },
}));

// 스크린 모드 고정된 화면 높이 값 조절 기능 보임 여부
const isVisibleControllerHeight = computed<boolean>(() => {
  return callStore.qrCodeInfo.options.screenHeight;
});

const languageDefault = computed<Language | null>(() => {
  const code = data.languageCode;

  if (!code) return audioStore.languageSelected;

  return typeof code === 'string' ? getLanguageByCode(code) : code;
});

watch(
  () => userStore.userPreferences,
  (newVal) => {
    if (newVal) {
      Object.assign(data, {
        languageCode: newVal.screenLanguageCode,
        textSizeMultiplier: newVal.textSizeMultiplier,
        screenMessageWidth: newVal.screenMessageWidth,
        screenContainerHeight: newVal.screenContainerHeight,
        screenScrollHeight: newVal.screenScrollHeight,
      });
    }
  },
  { immediate: true, deep: true, flush: 'post' }
);

defineExpose({
  payloadMessageFc,
});
</script>

<style scoped lang="scss">
.container-full-screen {
  width: 100%;
}

.container-control-screen {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  height: 100%;
}

.wrapper-features {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  height: 100%;
}

.wrapper-actions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
</style>
